import {
  customElement,
  DeesElement,
  property,
  html,
  cssManager,
  css,
  type TemplateResult,
} from '@design.estate/dees-element';

import type * as interfaces from '../../ts/ffb.interfaces.js';

@customElement('dotglobal-propose-modal')
export class DotglobalProposeModal extends DeesElement {
  @property({ type: Object })
  public accessor repo: interfaces.IPublicRepo | null = null;

  public static styles = [
    cssManager.defaultStyles,
    css`
      :host {
        display: contents;
        color: var(--text-primary, #0a0a0a);
        font-family: var(--font-sans);
      }

      * {
        box-sizing: border-box;
      }

      button {
        border: 0;
        background: none;
        color: inherit;
        font: inherit;
        cursor: pointer;
      }

      dees-icon {
        display: inline-flex;
        flex-shrink: 0;
      }

      .overlay {
        position: fixed;
        inset: 0;
        z-index: 100;
        display: flex;
        align-items: flex-start;
        justify-content: center;
        padding: 80px 20px;
        background: rgba(0, 0, 0, 0.55);
        backdrop-filter: blur(2px);
        animation: fade 0.15s ease;
      }

      .modal {
        width: min(560px, 100%);
        overflow: hidden;
        border: 1px solid var(--border-default, #e4e4e7);
        border-radius: 12px;
        background: var(--bg-primary, #ffffff);
        box-shadow: var(--shadow-lg, 0 4px 12px rgba(0, 0, 0, 0.15));
        animation: pop 0.18s cubic-bezier(0.16, 1, 0.3, 1);
      }

      .modal-h {
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 16px 18px;
        border-bottom: 1px solid var(--border-default, #e4e4e7);
      }

      .modal-title {
        font-family: var(--font-display);
        font-size: 15px;
        font-weight: 600;
        letter-spacing: -0.01em;
      }

      .modal-b {
        display: flex;
        flex-direction: column;
        gap: 16px;
        padding: 18px;
      }

      .modal-f {
        display: flex;
        justify-content: flex-end;
        gap: 8px;
        padding: 14px 18px;
        border-top: 1px solid var(--border-default, #e4e4e7);
        background: var(--bg-secondary, #fafafa);
      }

      .grow {
        flex: 1;
      }

      .sec {
        color: var(--text-secondary, #333333);
        font-size: 13.5px;
        line-height: 1.6;
      }

      .muted {
        color: var(--text-muted, #71717a);
      }

      .btn {
        height: 34px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 7px;
        padding: 0 14px;
        border: 1px solid var(--border-strong, #cccccc);
        border-radius: 6px;
        color: var(--text-primary, #0a0a0a);
        background: var(--bg-tertiary, #f4f4f5);
        font-size: 13px;
        font-weight: 500;
        white-space: nowrap;
      }

      .btn.primary {
        color: var(--accent-fg, #ffffff);
        border-color: var(--accent, #3b82f6);
        background: var(--accent, #3b82f6);
      }

      .iconbtn {
        width: 34px;
        height: 34px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border-radius: 6px;
        color: var(--text-secondary, #333333);
      }

      .iconbtn:hover {
        color: var(--text-primary, #0a0a0a);
        background: var(--hover, rgba(0, 0, 0, 0.04));
      }

      .agent-glyph {
        width: 32px;
        height: 32px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        border-radius: 6px;
        color: #8b5cf6;
        background: color-mix(in srgb, #8b5cf6 18%, transparent);
      }

      .copyfield {
        height: 36px;
        display: flex;
        align-items: center;
        overflow: hidden;
        border: 1px solid var(--border-default, #e4e4e7);
        border-radius: 6px;
        background: var(--bg-tertiary, #f4f4f5);
      }

      .copyfield .v {
        flex: 1;
        min-width: 0;
        overflow: hidden;
        padding: 0 12px;
        color: var(--text-primary, #0a0a0a);
        font-family: var(--font-mono);
        font-size: 12.5px;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .copyfield button {
        width: 38px;
        height: 100%;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        border-left: 1px solid var(--border-default, #e4e4e7);
        color: var(--text-secondary, #333333);
      }

      .term {
        overflow: hidden;
        border: 1px solid var(--border-strong, #cccccc);
        border-radius: 6px;
        background: #0a0a0a;
        color: #d4d4d4;
        font-family: var(--font-mono);
        font-size: 12.5px;
        line-height: 1.7;
      }

      .term-h {
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 8px 12px;
        border-bottom: 1px solid var(--border-strong, #cccccc);
      }

      .term-dot {
        width: 11px;
        height: 11px;
        border-radius: 50%;
      }

      .term-b {
        padding: 12px 14px;
        white-space: pre-wrap;
      }

      @keyframes fade {
        from { opacity: 0; }
      }

      @keyframes pop {
        from { opacity: 0; transform: translateY(8px) scale(0.99); }
      }
    `,
  ];

  public render(): TemplateResult {
    const command = `foss propose ${this.repo ? this.repo.org + '/' + this.repo.name : 'owner/repo'}`;
    return html`
      <div class="overlay" @mousedown=${(eventArg: MouseEvent) => { if (eventArg.target === eventArg.currentTarget) this.emitClose(); }}>
        <div class="modal" role="dialog" aria-modal="true">
          <div class="modal-h">
            <div class="agent-glyph">${this.icon('Sparkles')}</div>
            <div class="modal-title">Propose a change${this.repo ? ` to ${this.repo.name}` : ''}</div>
            <span class="grow"></span>
            <button class="iconbtn" @click=${() => this.emitClose()} aria-label="Close">${this.icon('X')}</button>
          </div>
          <div class="modal-b">
            <div class="sec">
              foss.global has no forks or pull requests. Instead, the open-source <b>foss propose</b> agent investigates a fix on your machine and files it as a reviewable patch.
            </div>
            <div>
              <div class="muted" style="font-size: 12px; margin-bottom: 6px">1 · Install the agent</div>
              ${this.renderCopyField('pnpm add -g @foss.global/propose')}
            </div>
            <div>
              <div class="muted" style="font-size: 12px; margin-bottom: 6px">2 · Run it in your local clone</div>
              ${this.renderCopyField(command)}
            </div>
            ${this.renderTermDemo()}
          </div>
          <div class="modal-f">
            <button class="btn" @click=${() => { this.emitNavigate('#/agent'); this.emitClose(); }}>Read the full guide</button>
            <button class="btn primary" @click=${() => this.emitCopy(command)}>${this.icon('ArrowRight')} Copy command</button>
          </div>
        </div>
      </div>
    `;
  }

  private renderCopyField(valueArg: string): TemplateResult {
    return html`
      <div class="copyfield">
        <div class="v">${valueArg}</div>
        <button title="Copy" @click=${() => this.emitCopy(valueArg)}>${this.icon('Copy')}</button>
      </div>
    `;
  }

  private renderTermDemo(): TemplateResult {
    return html`
      <div class="term">
        <div class="term-h">
          <span class="term-dot" style="background: #ef4444"></span>
          <span class="term-dot" style="background: #f59e0b"></span>
          <span class="term-dot" style="background: #22c55e"></span>
          <span style="margin-left: 6px; font-size: 11px; color: #888">~/code/${this.repo?.name || 'repo'}</span>
        </div>
        <div class="term-b"><span style="color: #22c55e">$ </span><span style="color: #fafafa">foss propose "describe the issue"</span>
<span style="color: #6b7280"># reading working tree</span>
<span style="color: #6b7280"># reproducing... </span><span style="color: #22c55e">reproduced locally</span>
<span style="color: #6b7280"># running test suite... </span><span style="color: #22c55e">passing with fix</span>
<span style="color: #6b7280"># drafting patch... </span><span style="color: #3b82f6">changed files ready</span>

<span style="color: #22c55e">✓ </span><span style="color: #fafafa">Filed proposal issue - patch attached</span></div>
      </div>
    `;
  }

  private emitClose(): void {
    this.dispatchEvent(new CustomEvent('dotglobal-close', { bubbles: true, composed: true }));
  }

  private emitNavigate(hashArg: string): void {
    this.dispatchEvent(new CustomEvent('dotglobal-navigate', {
      detail: { hash: hashArg },
      bubbles: true,
      composed: true,
    }));
  }

  private emitCopy(textArg: string): void {
    this.dispatchEvent(new CustomEvent('dotglobal-copy', {
      detail: { text: textArg },
      bubbles: true,
      composed: true,
    }));
  }

  private icon(nameArg: string): TemplateResult {
    return html`<dees-icon .icon=${`lucide:${nameArg}`} .iconSize=${16}></dees-icon>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'dotglobal-propose-modal': DotglobalProposeModal;
  }
}
